import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { isImageTag } from '../../../services/visualizationService';
import TaggedVizImage, { VizImage } from './VizImage';

interface DraftBodyProps {
  text: string;
}

const components: Components = {
  h1: ({ children }) => <h1 className="text-[22px] font-bold text-[#0a0a0a] mt-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-[17px] font-semibold text-[#0a0a0a] mt-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-[15px] font-semibold text-[#0a0a0a] mt-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-[14px] font-semibold text-[#0a0a0a]">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-[#0a0a0a] leading-[1.6]">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 text-sm text-[#0a0a0a] leading-[1.6] space-y-1">{children}</ul>,
  ol: ({ children }) => (
    <ol className="list-decimal pl-6 text-sm text-[#0a0a0a] leading-[1.6] space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="text-sm text-[#0a0a0a] leading-[1.6]">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#0a0a0a]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-[#171717] underline underline-offset-2 hover:text-[#525252]"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-[#e5e5e5] pl-3 text-sm text-[#525252] italic">{children}</blockquote>
  ),
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <pre className="overflow-x-auto rounded-md bg-[#f5f5f5] border border-[#e5e5e5] p-3 text-[12px] text-[#0a0a0a] font-mono leading-[1.5]">
          <code>{children}</code>
        </pre>
      );
    }
    return (
      <code className="rounded bg-[#f5f5f5] border border-[#e5e5e5] px-1 py-0.5 text-[12px] font-mono text-[#0a0a0a]">
        {children}
      </code>
    );
  },
  table: ({ children }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#f5f5f5]">{children}</thead>,
  th: ({ children }) => <th className="border border-[#e5e5e5] px-3 py-2 font-semibold text-[#0a0a0a]">{children}</th>,
  td: ({ children }) => <td className="border border-[#e5e5e5] px-3 py-2 text-[#0a0a0a]">{children}</td>,
  hr: () => <hr className="border-t border-[#e5e5e5]" />,
  img: ({ src, alt }) => {
    if (!src) return null;
    if (isImageTag(src)) return <TaggedVizImage tag={src} alt={alt} />;
    return <VizImage src={src} alt={alt} />;
  },
};

// react-markdown strips unknown URL schemes by default, which would drop our
// `xqviz://` / `xqsurviz://` tags.
const urlTransform = (url: string) => (isImageTag(url) ? url : defaultUrlTransform(url));

const DraftBody = ({ text }: DraftBodyProps) => {
  const trimmed = text?.trim() ?? '';
  if (!trimmed) {
    return <p className="text-sm text-[#a3a3a3] italic">No content yet.</p>;
  }
  return (
    <div className="flex flex-col gap-4">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>
        {trimmed}
      </ReactMarkdown>
    </div>
  );
};

export default DraftBody;
